<template>
  <div ref="wrapper" class="tab-transition">
    <Transition
      :css="false"
      mode="out-in"
      @before-leave="beforeLeave"
      @leave="leave"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
    >
      <div :key="activeKey" class="tab-transition__content">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';

// Tab-switch animation, in order:
//   1. card keeps its current size while the outgoing content fades out;
//   2. content swaps (hidden);
//   3. the card height animates to the new content's height (grow/shrink);
//   4. the new content fades in.
// Sequencing is time-based (not transitionend-based) so content always ends visible.
defineProps<{ activeKey: string }>();

const wrapper = ref<HTMLElement>();
const fadeMs = 160;
const growMs = 300;
let lockedHeight = 0;

// True only while a tab swap is animating. The height we pin during the swap is a snapshot of the
// content at that instant — but tab panels load data async (charts, comparable rentals), so the
// content can grow *after* the snapshot. A ResizeObserver releases the pinned height once the swap
// is done and the content settles, so late-arriving content can never stay clipped or overflow the
// card onto the sections below it.
let transitioning = false;
let resizeObserver: ResizeObserver | null = null;

function observeContent(content: Element) {
  if (typeof ResizeObserver === 'undefined') return;

  if (!resizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      if (transitioning || !wrapper.value) return;
      // Idle: the wrapper must follow its content. Drop any height left pinned by the grow step.
      if (wrapper.value.style.height) wrapper.value.style.height = '';
    });
  }

  resizeObserver.disconnect();
  resizeObserver.observe(content);
}

function beforeLeave() {
  if (!wrapper.value) return;
  transitioning = true;
  // Pin the wrapper to its current height so it doesn't collapse during the swap.
  lockedHeight = wrapper.value.offsetHeight;
  wrapper.value.style.height = `${lockedHeight}px`;
  wrapper.value.style.overflow = 'hidden';
}

function leave(el: Element, done: () => void) {
  const element = el as HTMLElement;
  element.style.transition = `opacity ${fadeMs}ms ease`;
  requestAnimationFrame(() => {
    element.style.opacity = '0';
  });
  setTimeout(done, fadeMs);
}

function beforeEnter(el: Element) {
  (el as HTMLElement).style.opacity = '0';
}

function enter(el: Element, done: () => void) {
  const element = el as HTMLElement;

  if (!wrapper.value) {
    element.style.opacity = '1';
    done();
    return;
  }

  const fadeIn = () => {
    element.style.transition = `opacity ${fadeMs}ms ease`;
    requestAnimationFrame(() => {
      element.style.opacity = '1';
    });
    setTimeout(done, fadeMs);
  };

  // Target the height the wrapper will actually settle at, measured by briefly letting it size
  // to its content. element.scrollHeight overestimates when content uses negative margins (e.g.
  // the horizontal-scroll "bleed" container's -2rem), which made the sections below jump when the
  // pinned height was released after the animation.
  const pinnedHeight = wrapper.value.style.height;
  wrapper.value.style.height = 'auto';
  const targetHeight = wrapper.value.offsetHeight;
  wrapper.value.style.height = pinnedHeight;

  // No meaningful height change → skip the grow step and just fade the content in.
  if (Math.abs(targetHeight - lockedHeight) < 1) {
    fadeIn();
    return;
  }

  wrapper.value.style.transition = `height ${growMs}ms ease`;
  requestAnimationFrame(() => {
    if (wrapper.value) wrapper.value.style.height = `${targetHeight}px`;
  });
  setTimeout(fadeIn, growMs);
}

function afterEnter(el: Element) {
  const element = el as HTMLElement;
  element.style.opacity = '';
  element.style.transition = '';

  transitioning = false;

  if (!wrapper.value) return;
  // Release to natural height and restore overflow so inner card shadows aren't clipped.
  wrapper.value.style.height = '';
  wrapper.value.style.transition = '';
  wrapper.value.style.overflow = '';

  // Keep following this panel's content while it's the active one (it may still be loading data).
  observeContent(element);
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>
